import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Check } from "lucide-react";
import { api } from "@/lib/api";

const Services = () => {
  const [treatments, setTreatments] = useState([]);

  useEffect(() => {
    api.get("/treatments").then((r) => setTreatments(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="services-page">
      <section className="px-6 md:px-12 lg:px-20 pt-24 pb-16 md:pt-32">
        <p className="overline mb-6">Panchakarma & Therapies</p>
        <h1 className="text-5xl sm:text-7xl tracking-tight font-light max-w-4xl leading-[1.05]">
          The five classical actions and the <em className="font-display italic" style={{ color: "var(--ayu-secondary)" }}>therapies</em> that complete them.
        </h1>
        <p className="mt-8 max-w-2xl leading-relaxed" style={{ color: "var(--ayu-body)" }}>
          Panchakarma is not a procedure to be improvised at home. It is administered under
          continuous clinical supervision, with a careful preparatory phase before, and a slow
          re-integration after.
        </p>
      </section>

      <section className="px-6 md:px-12 lg:px-20 pb-24">
        <div className="space-y-24">
          {treatments.map((t, i) => (
            <article
              key={t.id}
              id={t.slug}
              data-testid={`treatment-${t.slug}`}
              className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center scroll-mt-24 ${i % 2 ? "lg:[&>div:first-child]:order-2" : ""}`}
            >
              <div className="lg:col-span-6">
                <img src={t.image} alt={t.name} className="w-full h-[55vh] object-cover" loading="lazy" />
              </div>
              <div className="lg:col-span-6">
                <p className="overline mb-3">{t.sanskrit_name}</p>
                <h2 className="text-4xl sm:text-5xl leading-tight mb-2">{t.name}</h2>
                <div className="flex items-center gap-2 mb-6 text-sm" style={{ color: "var(--ayu-muted)" }}>
                  <Clock size={14} strokeWidth={1.5} /> <span>{t.duration}</span>
                </div>
                <p className="leading-relaxed mb-8" style={{ color: "var(--ayu-body)" }}>{t.description}</p>
                <ul className="space-y-2 mb-8">
                  {t.benefits.map((b) => (
                    <li key={b} className="flex gap-3 text-sm">
                      <Check size={16} strokeWidth={1.5} style={{ color: "var(--ayu-secondary)" }} className="mt-1 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact" data-testid={`enquire-${t.slug}`} className="btn-secondary">Enquire about {t.name}</Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-28 text-center" style={{ background: "var(--ayu-surface)" }}>
        <p className="overline mb-6">Three Phase Approach</p>
        <h2 className="text-3xl sm:text-4xl max-w-3xl mx-auto mb-12 leading-tight">
          Every Panchakarma unfolds in three careful phases.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-left max-w-5xl mx-auto">
          {[
            { n: "Purvakarma", d: "Preparatory oleation (Snehana) and sweating (Swedana) — softening the tissues so that toxins may release." },
            { n: "Pradhanakarma", d: "The main cleansing action — Vamana, Virechana, Basti, Nasya, or Raktamokshana, depending on prakriti & vikriti." },
            { n: "Paschatkarma", d: "Slow re-integration — gradual diet, gentle activity, herbal rasayanas to rebuild ojas." },
          ].map((p, i) => (
            <div key={i} data-testid={`phase-${i}`}>
              <p className="font-display text-5xl mb-4" style={{ color: "var(--ayu-secondary)" }}>0{i + 1}</p>
              <h3 className="text-2xl mb-3">{p.n}</h3>
              <p className="text-sm leading-relaxed" style={{ color: "var(--ayu-body)" }}>{p.d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Services;
